/**
 * Expense Service (SLIK #4, 2026-08-27) — the data layer for the Register → Expenses tab.
 * Simple shop-expense ledger: log a cost (supplies, rent, utilities…), list by date range, delete.
 * Mirrors the codebase idiom (Firestore `expenses` collection, one-shot getDocs + refetch after
 * mutations — expenses are low-frequency so no live listener needed). `shopId` is a soft field
 * (matches blockedTimesService) for future multi-tenant filtering; today the app is single-shop.
 *
 * Consumers: components/ExpensesTab.tsx (the Register tab). NOT yet wired into reporting/P&L —
 * that's a deliberate follow-up (net-profit = revenue − expenses) noted in the memory pin.
 */
import { collection, addDoc, deleteDoc, doc, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { scopedQuery, ensureActiveShopId } from './tenantQuery';

const COLLECTION = 'expenses';

export type ExpensePaymentMethod = 'cash' | 'card' | 'transfer' | 'other';

export interface Expense {
  id: string;
  date: string;             // YYYY-MM-DD (the day the cost was incurred)
  amount: number;           // positive dollars
  category: string;         // one of EXPENSE_CATEGORIES (free-text tolerated)
  vendor?: string;          // who was paid (e.g. "CosmoProf", "PSE&G")
  description?: string;     // short note
  paymentMethod: ExpensePaymentMethod;
  shopId?: string;          // soft multi-tenant tag
  createdByUid?: string;
  createdByName?: string;
  createdAt?: Date;
  // Statement-import provenance (2026-09-04) — set when this row came from a dropped bank statement.
  importBatchId?: string;   // which import batch created it (for one-click undo)
  dedupHash?: string;       // date|amount|normalized-description — blocks re-importing the same txn
  bankDescription?: string; // the raw statement line, kept verbatim for audit
}

/** Preset categories for the picker (free-text still allowed in the doc). */
export const EXPENSE_CATEGORIES = [
  'Supplies',
  'Product Inventory',
  'Rent',
  'Utilities',
  'Payroll',
  'Contractor / Commission',
  'Marketing',
  'Equipment',
  'Maintenance',
  'Software / Subscriptions',
  'Insurance',
  'Merchant / Bank Fees',
  'Professional Fees',
  'Travel / Auto',
  'Meals / Entertainment',
  'Taxes / Fees',
  'Other',
] as const;

export interface NewExpenseInput {
  date: string;
  amount: number;
  category: string;
  vendor?: string;
  description?: string;
  paymentMethod: ExpensePaymentMethod;
  shopId?: string;
  createdByUid?: string;
  createdByName?: string;
  importBatchId?: string;
  dedupHash?: string;
  bankDescription?: string;
}

class ExpenseService {
  /** All expenses, newest first. (One-shot read — call again after add/delete to refresh.) */
  async listExpenses(): Promise<Expense[]> {
    try {
    await ensureActiveShopId();
    const snap = await getDocs(scopedQuery(collection(db, COLLECTION), orderBy('date', 'desc')));
    return snap.docs.map((d) => {
      const data = d.data() as any;
      return {
        id: d.id,
        date: data.date || '',
        amount: Number(data.amount) || 0,
        category: data.category || 'Other',
        vendor: data.vendor || '',
        description: data.description || '',
        paymentMethod: (data.paymentMethod || 'other') as ExpensePaymentMethod,
        shopId: data.shopId,
        createdByUid: data.createdByUid,
        createdByName: data.createdByName,
        createdAt: data.createdAt?.toDate?.() || undefined,
        importBatchId: data.importBatchId,
        dedupHash: data.dedupHash,
        bankDescription: data.bankDescription,
      };
    });
    } catch (e) {
      // expenses is owner/manager-only (Phase 0). Non-manager context → permission-denied; degrade
      // to empty instead of throwing into the Register/Expenses UI.
      console.warn('listExpenses denied/failed', e);
      return [];
    }
  }

  /** Create an expense. Strips undefined fields (Firestore rejects them). Returns the new doc id. */
  async addExpense(input: NewExpenseInput): Promise<string> {
    const doc: Record<string, any> = {
      date: input.date,
      amount: Number(input.amount) || 0,
      category: input.category || 'Other',
      paymentMethod: input.paymentMethod || 'other',
      createdAt: new Date(),
    };
    if (input.vendor) doc.vendor = input.vendor;
    if (input.description) doc.description = input.description;
    if (input.shopId) doc.shopId = input.shopId;
    if (input.createdByUid) doc.createdByUid = input.createdByUid;
    if (input.createdByName) doc.createdByName = input.createdByName;
    if (input.importBatchId) doc.importBatchId = input.importBatchId;
    if (input.dedupHash) doc.dedupHash = input.dedupHash;
    if (input.bankDescription) doc.bankDescription = input.bankDescription;
    const ref = await addDoc(collection(db, COLLECTION), doc);
    return ref.id;
  }

  async deleteExpense(id: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, id));
  }

  /** Undo a statement import: delete every expense row created by that batch. Returns count removed. */
  async deleteByImportBatch(importBatchId: string): Promise<number> {
    const snap = await getDocs(query(collection(db, COLLECTION), where('importBatchId', '==', importBatchId)));
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
    return snap.size;
  }
}

export const expenseService = new ExpenseService();
export default expenseService;
